// src/utils/marketing.js
export const dlPush = (obj) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(obj);
};

// GA4 items a partir do shape Puríssima
export const toGa4Items = (items=[]) =>
  items.map(it => ({
    item_id: String(it.var_id ?? it.id),
    item_name: it.label,
    item_variant: [it.slot, it.format].filter(Boolean).join(' / '),
    price: Number(it.price ?? 0),
    quantity: Number(it.quantity ?? 1),
    item_category: it.formulaLabel
  }));

// Soma valor de itens (preço * quantidade)
export const itemsTotalValue = (items = []) =>
  items.reduce((acc, it) => acc + Number(it.price ?? 0) * Number(it.quantity ?? 1), 0);

// Dispara um evento genérico no dataLayer (atalho)
export const fireEvent = (event, params = {}) => dlPush({ event, ...params });

// Resumo da compra inteira (assinaturas trimestral/anual são cobradas de uma vez),
// salvo no checkout porque o pedido devolvido pelo backend traz só o valor de 1 entrega mensal.
const purchaseSummaryKey = (invoiceId) => `purchase_summary_${invoiceId}`;

export const savePurchaseSummary = (invoiceId, summary) => {
  if (!invoiceId || !summary) return;
  try {
    localStorage.setItem(purchaseSummaryKey(invoiceId), JSON.stringify(summary));
  } catch (e) {
    // storage indisponível: firePurchase cai no valor do pedido
  }
};

const readPurchaseSummary = (invoiceId) => {
  if (!invoiceId) return null;
  try {
    return JSON.parse(localStorage.getItem(purchaseSummaryKey(invoiceId)));
  } catch (e) {
    return null;
  }
};

export const clearPurchaseSummary = (invoiceId) => {
  if (!invoiceId) return;
  try {
    localStorage.removeItem(purchaseSummaryKey(invoiceId));
  } catch (e) {}
};

export const clearAllPurchaseSummaries = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("purchase_summary_"))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) {}
};

// Disparo de purchase
export const firePurchase = (order) => {
  if (!order) return;
  const eventId = order.event_id || order.order_uuid || order.ord_uuid; // use o que vier
  const summary =
    readPurchaseSummary(order.event_id) ||
    (order.invoice_id ? readPurchaseSummary(order.invoice_id) : null);
  const multiplier = Number(summary?.multiplier) > 1 ? Number(summary.multiplier) : 1;
  const totalValue = Number(summary?.value) > 0 ? Number(summary.value) : Number(order.total ?? 0);
  const shipping = Number(summary?.shipping) > 0 ? Number(summary.shipping) : Number(order.shipping ?? 0);
  const items = toGa4Items(order.items || []).map((it) => ({
    ...it,
    quantity: it.quantity * multiplier
  }));
  dlPush({
    event: 'purchase',
    event_id: eventId,
    transaction_id: String(order.ord_id),
    currency: 'BRL',
    value: totalValue,
    shipping,
    tax: Number(order.tax ?? 0),
    payment_type: order.payment_method,
    items
  });
};

// CompleteRegistration (ou GA4 sign_up)
// Ex.: fireCompleteRegistration({ user_id, method: 'MapaDaSaude', eventName: 'CompleteRegistration' })
export const fireCompleteRegistration = ({
  user_id,
  method = 'MapaDaSaude',
  value,
  currency = 'BRL',
  eventName = 'CompleteRegistration', // use 'sign_up' se quiser só o GA4 padrão
  alsoGa4 = true, // além do custom, dispara também o GA4 recomendado
} = {}) => {
  if (!eventName) return;
  dlPush({ event: eventName, user_id, method, value, currency });
  if (alsoGa4 && eventName !== 'sign_up') {
    dlPush({ event: 'sign_up', user_id, method, value, currency });
  }
};

// AddToCart (ou GA4 add_to_cart)
// Aceita "item" único ou lista "items" no shape Puríssima
// Ex.: fireAddToCart({ item, value }) ou fireAddToCart({ items })
export const fireAddToCart = ({
  item,
  items,
  value,
  currency = 'BRL',
  eventName = 'AddToCart', // use 'add_to_cart' se quiser só o GA4 padrão
  alsoGa4 = true,
} = {}) => {
  const list = Array.isArray(items) ? items : (item ? [item] : []);
  const gaItems = toGa4Items(list);
  const computedValue = value != null ? Number(value) : itemsTotalValue(list);

  dlPush({ event: eventName, currency, value: computedValue, items: gaItems });
  if (alsoGa4 && eventName !== 'add_to_cart') {
    dlPush({ event: 'add_to_cart', currency, value: computedValue, items: gaItems });
  }
};

// InitiateCheckout (ou GA4 begin_checkout)
// Dispara ao acessar a página de checkout com itens no carrinho
// Ex.: fireInitiateCheckout({ items, value })
export const fireInitiateCheckout = ({
  items,
  value,
  currency = 'BRL',
  eventName = 'InitiateCheckout', // use 'begin_checkout' se quiser só o GA4 padrão
  alsoGa4 = true,
} = {}) => {
  const list = Array.isArray(items) ? items : [];
  const gaItems = toGa4Items(list);
  const computedValue = value != null ? Number(value) : itemsTotalValue(list);

  dlPush({ event: eventName, currency, value: computedValue, items: gaItems });
  if (alsoGa4 && eventName !== 'begin_checkout') {
    dlPush({ event: 'begin_checkout', currency, value: computedValue, items: gaItems });
  }
};
